/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { Select, Button } from '@chakra-ui/react';

type FeeGroup = {
    id: string;
    name: string;
    template: any;
};

type Method = {
    id: string;
    name: string;
};

const AgentAssignFeeGroupModal: React.FC<{
    setSwalProps: any;
    agentDetail: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
}> = ({ setSwalProps, isOpen, onClose, agentDetail, triggerReload }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isFetchingMethodAndFeeGroup, setIsFetchingMethodAndFeeGroup] = useState<boolean>(false);
    const [assetList, setAssetList] = useState<any>(null);
    const [methodList, setMethodList] = useState<any>(null);
    const [feeGroupList, setFeeGroupList] = useState<any>(null);
    const [feeName, setFeeName] = useState<string>("");
    const [asset, setAsset] = useState<string>("");
    const [methodId, setMethodId] = useState<string>("");
    const [feeGroupId, setFeeGroupId] = useState<string>("");
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<any>("");

    const fetchAssets = async () => {
        try {
            let requestUrl = `/api/datasource/assets`;

            const response = await axios.get(
                process.env.REACT_APP_API_URL +
                requestUrl
            );

            const data = response?.data;

            setAssetList(data);
        } catch (error) {
            console.error("Error fetching assets", error);
        }
    };

    function getMethodNameByMethodId(methodId: string): string | undefined {
        const method = methodList.find((x: any) => x.id === methodId);
        return method?.name;
    }

    const closeModal = async () => {
        onClose();
        setErrorMessage("");
        setFeeName("");
        setAsset("");
        setAssetList(null);
        setMethodId("");
        setMethodList(null);
        setFeeGroupId("");
        setFeeGroupList(null);
    };

    const submitHandler = async () => {
        setErrorMessage("");

        const methodName = getMethodNameByMethodId(methodId);
        const data = {
            name: feeName,
            agnt_id: agentDetail.id,
            asset: asset,
            method_id: methodId,
            feegroup_id: feeGroupId,
            username: localStorage.getItem("username"),
            userRole: localStorage.getItem("userRole")?.toLowerCase(),
            agentName: agentDetail.suba_name,
            methodName: methodName
        }

        try {
            setIsProcessing(true);
            await axios.post(
                process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole")?.toLowerCase()}/agent/action/agent-assign-fee-group`,
                data
            )
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Assign fee group to agent successfully",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                                triggerReload();
                            },
                        });
                    } else {
                        setIsProcessing(false);
                        setErrorMessage('Assign fee group to agent has been failed');
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    if (error.response) {
                        setErrorMessage(error.response.data.message);
                    } else {
                        setErrorMessage('Assign fee group to agent has been failed');
                    }
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const handleAssetDropdownChange = async (event: any) => {
        setMethodId("");
        setFeeGroupId("");
        setFeeGroupList("");

        const { value } = event.target;
        setAsset(value);
        if (value !== "") {
            setIsFetchingMethodAndFeeGroup(true);
            await getAgentFeeGroupListByAsset(value);
            setIsFetchingMethodAndFeeGroup(false);
        }
    };

    const getAgentFeeGroupListByAsset = async (asset: any) => {
        try {
            const response = await axios.get(
                process.env.REACT_APP_API_URL +
                `/api/agent/feegroups?asset=${asset}`
            );

            const responseData = response.data?.feeGroups?.payload;
            const feeGroups: FeeGroup[] = [];
            const methods: Method[] = [];

            const feeGroupMap = new Set<string>();
            for (const fg of responseData.feegroups) {
                if (!feeGroupMap.has(fg.feegroup_id)) {
                    feeGroups.push({
                        id: fg.feegroup_id,
                        name: fg.name,
                        template: fg.feetemplate,
                    });
                    feeGroupMap.add(fg.feegroup_id);
                }
            }

            const methodKeySet = new Set<string>();
            for (const provider of responseData.provider) {
                for (const cls of provider.classes) {
                    const key = `${cls.method_id}-${cls.classfile}`;
                    if (!methodKeySet.has(key)) {
                        methods.push({
                            id: cls.method_id,
                            name: `${cls.method_name} (${provider.provider_name}) (${cls.classfile})`,
                        });
                        methodKeySet.add(key);
                    }
                }
            }

            setMethodList(methods);
            setFeeGroupList(feeGroups);
        } catch (error) {
            console.error("Error fetching data", error);
        }
    };

    const handleMethodDropdownChange = async (event: any) => {
        const { value } = event.target;
        if (value !== "") {
            setMethodId(value);
        } else {
            setMethodId("");
        }
    };

    const handleFeeGroupDropdownChange = async (event: any) => {
        const { value } = event.target;
        if (value !== "") {
            setFeeGroupId(value);
        } else {
            setFeeGroupId("");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            await fetchAssets();
            setIsLoading(false);
        };

        if (isOpen && !assetList) {
            fetchData();
        }
    }, [isOpen]); // eslint-disable-line

    const isFormValid = feeName !== "" && asset !== "" && methodId !== "" && feeGroupId !== ""

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                <ModalOverlay className="bg-[#000] !opacity-30" />
                <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                    <ModalBody className="p-6 md:p-7">
                        <div className="mb-4 flex items-start gap-3">
                            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-5 w-5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M12 4v16m8-8H4" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Assign Fee Group
                                </h1>
                                <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                    Configure fee group assignment for {agentDetail?.suba_name}
                                </p>
                            </div>
                        </div>

                        <div className="mb-6 space-y-4">
                            <div>
                                <InputField
                                    disabled={isProcessing}
                                    variant="auth"
                                    extra="shadow-sm"
                                    label="Name"
                                    required={true}
                                    placeholder="Enter name"
                                    id="name"
                                    value={feeName}
                                    type="text"
                                    autoComplete="off"
                                    onChange={e => setFeeName(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-sm text-navy-700 dark:text-white font-medium">
                                    Asset <span className="text-red-500">*</span>
                                </label>
                                <Select required={true} disabled={isProcessing || isLoading || !assetList} id="asset" value={asset} onChange={handleAssetDropdownChange} className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm">
                                    <option value="">{assetList && assetList.length > 0 ? "-- Please Select --" : "Retrieving..."}</option>
                                    {assetList && assetList.filter((item: any) => !item.crypto).length > 0 &&
                                        <optgroup label="Fiat">
                                            {assetList && assetList.length > 0 && assetList.filter((item: any) => !item.crypto).map((item: any) => (
                                                <option key={item.name} value={item.name} data-decimal={item.decimal}>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    }
                                    {assetList && assetList.filter((item: any) => item.crypto).length > 0 &&
                                        <optgroup label="Crypto">
                                            {assetList && assetList.length > 0 && assetList.filter((item: any) => item.crypto).map((item: any) => (
                                                <option key={item.name} value={item.name} data-decimal={item.decimal}>
                                                    {item.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    }
                                </Select>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm text-navy-700 dark:text-white font-medium">
                                    Method <span className="text-red-500">*</span>
                                </label>
                                <Select required={true} disabled={isProcessing || isLoading || isFetchingMethodAndFeeGroup || !methodList} id="method" value={methodId} onChange={handleMethodDropdownChange} className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm">
                                    <option value="">{!isFetchingMethodAndFeeGroup ? "-- Please Select --" : "Retrieving..."}</option>
                                    {methodList && methodList.length > 0 && methodList.map((item: any) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </Select>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm text-navy-700 dark:text-white font-medium">
                                    Fee Group <span className="text-red-500">*</span>
                                </label>
                                <Select required={true} disabled={isProcessing || isLoading || isFetchingMethodAndFeeGroup || !feeGroupList} id="feegroup" value={feeGroupId} onChange={handleFeeGroupDropdownChange} className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm">
                                    <option value="">{!isFetchingMethodAndFeeGroup ? "-- Please Select --" : "Retrieving..."}</option>
                                    {feeGroupList && feeGroupList.length > 0 && feeGroupList.map((item: any) => (
                                        <option key={item.id} value={item.id}>{item.name} (fixed {item.template.fixed} / {item.template.percentage}%)</option>
                                    ))}
                                </Select>
                            </div>

                            {errorMessage && (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
                                    {errorMessage}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button
                                onClick={() => closeModal()}
                                isDisabled={isProcessing}
                                className="btn btn-default"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => submitHandler()}
                                type="submit"
                                isDisabled={!isFormValid || isProcessing}
                                isLoading={isProcessing}
                                loadingText='Processing...'
                                className="btn btn-primary">
                                Assign Fee Group
                            </Button>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

export default AgentAssignFeeGroupModal;